import React, { useEffect, useState } from 'react';
import Skeleton from '../components/Skeleton.js';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore.js';
import { api } from '../lib/api.js';

const getGamemodeEmoji = (gm: string) => {
  switch (gm) {
    case 'sword': return '⚔️';
    case 'mace': return '🔨';
    case 'mace_rockets': return '🚀';
    case 'cpvp': return '💎';
    case 'axe_shield': return '🛡️';
    case 'nethpot': return '🧪';
    case 'tank': return '🪖';
    default: return '🎮';
  }
};

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [stats, setStats] = useState({ lessonsDone: 0, rank: 0, clipsPosted: 0 });
  const [activeCourse, setActiveCourse] = useState<any>(null);
  const [topRanks, setTopRanks] = useState<any[]>([]);
  const [myRank, setMyRank] = useState<any>(null);
  const [recommendedCourses, setRecommendedCourses] = useState<any[]>([]);
  const [highlights, setHighlights] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        if (!user) return;

        const userStatsPromise = api.get(`/api/users/${user.username}/stats`);
        const userClipsPromise = api.get(`/api/users/${user.username}/clips`);
        const userRankPromise = api.get(`/api/leaderboard/me`);
        const leaderboardPromise = api.get(`/api/leaderboard`);
        const coursesPromise = api.get(`/api/courses`);
        const clipsPromise = api.get(`/api/clips?sort=recent`);

        const [
          userStatsRes,
          userClipsRes,
          userRankRes,
          leaderboardRes,
          coursesRes,
          clipsRes
        ] = await Promise.all([
          userStatsPromise.catch(() => ({ data: {} })),
          userClipsPromise.catch(() => ({ data: [] })),
          userRankPromise.catch(() => ({ data: { rank_position: 0 } })),
          leaderboardPromise.catch(() => ({ data: [] })),
          coursesPromise.catch(() => ({ data: [] })),
          clipsPromise.catch(() => ({ data: [] }))
        ]);

        let completedCount = 0;
        const statsData = userStatsRes.data || {};
        Object.keys(statsData).forEach((key) => {
          completedCount += statsData[key].completed || 0;
        });

        setStats({
          lessonsDone: completedCount,
          rank: userRankRes.data?.rank_position || 0,
          clipsPosted: userClipsRes.data?.length || 0
        });

        setMyRank(userRankRes.data);

        const allCourses = coursesRes.data || [];
        let chosenCourse: any = null;

        for (const c of allCourses) {
          try {
            const progRes = await api.get(`/api/courses/${c.id}/progress`);
            const pct = progRes.data?.percentage || 0;
            c.percentage = pct;
            c.progressData = progRes.data;
            if (pct > 0 && pct < 100) {
              chosenCourse = c;
              break;
            }
          } catch (e) {}
        }

        if (!chosenCourse && allCourses.length > 0) {
          chosenCourse = allCourses[0];
        }

        setActiveCourse(chosenCourse);

        const fullLeaderboard = leaderboardRes.data || [];
        setTopRanks(fullLeaderboard.slice(0, 2));
        setRecommendedCourses(allCourses.slice(0, 3));
        setHighlights(clipsRes.data.slice(0, 4));
      } catch (err) {
        console.error('Error fetching home dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant font-medium">
        <div className="w-full max-w-6xl px-gutter">
          <div className="grid grid-cols-12 gap-gutter">
            <div className="col-span-12 lg:col-span-8">
              <div className="h-56 rounded-lg overflow-hidden">
                <Skeleton />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-4 space-y-3">
              <Skeleton height={56} />
              <Skeleton height={56} />
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-40 rounded-lg overflow-hidden">
                <Skeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const welcomeBannerUrl =
    'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1400&auto=format&fit=crop&q=80';

  const getGamemodeColor = (gm: string) => {
    switch (gm) {
      case 'mace':
      case 'mace_rockets':
        return 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]';
      case 'sword':
      case 'cpvp':
        return 'bg-[rgba(224,60,60,0.15)] text-[#e03c3c]';
      case 'axe_shield':
        return 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]';
      default:
        return 'bg-[rgba(255,255,255,0.08)] text-on-surface-variant';
    }
  };

  const getGamemodeLabel = (gm: string) => {
    if (gm === 'cpvp') return 'Crystal PvP';
    if (gm === 'axe_shield') return 'Axe PvP';
    if (gm === 'mace_rockets') return 'Mace Rockets';
    return gm.charAt(0).toUpperCase() + gm.slice(1);
  };

  const handleResumeLesson = () => {
    if (!activeCourse) return;
    const progressLessons = activeCourse.progressData?.lessons || [];
    const firstIncomplete = progressLessons.find((l: any) => !l.completed);
    const lessonId = firstIncomplete ? firstIncomplete.lesson_id : (activeCourse.lessons?.[0]?.id || '');
    if (lessonId) {
      navigate(`/courses/${activeCourse.id}/lessons/${lessonId}`);
    } else {
      navigate(`/courses/${activeCourse.id}`);
    }
  };

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface">
      {/* Stats Row */}
      <div className="flex gap-stack-md mb-stack-lg flex-wrap select-none">
        <div className="px-4 py-2 bg-surface-container thin-border flex items-center gap-2 rounded">
          <span className="material-symbols-outlined text-[18px] text-[#e03c3c]">menu_book</span>
          <span className="font-label-caps text-label-caps text-on-surface-variant uppercase">
            {stats.lessonsDone} Lessons Done
          </span>
        </div>
        <div className="px-4 py-2 bg-surface-container thin-border flex items-center gap-2 rounded">
          <span className="material-symbols-outlined text-[18px] text-[#e03c3c]">military_tech</span>
          <span className="font-label-caps text-label-caps text-on-surface-variant uppercase">
            Rank #{stats.rank || 'N/A'}
          </span>
        </div>
        <div className="px-4 py-2 bg-surface-container thin-border flex items-center gap-2 rounded">
          <span className="material-symbols-outlined text-[18px] text-[#e03c3c]">videocam</span>
          <span className="font-label-caps text-label-caps text-on-surface-variant uppercase">
            {stats.clipsPosted} Clips Posted
          </span>
        </div>
      </div>

      {/* Welcome Banner */}
      <section className="relative mb-stack-lg rounded-lg overflow-hidden h-[240px] thin-border group select-none">
        <div className="absolute inset-0 z-0">
          <img
            alt="Background"
            className="w-full h-full object-cover grayscale opacity-40 group-hover:scale-105 transition-transform duration-700"
            src={welcomeBannerUrl}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent z-10"></div>
        <div className="relative z-20 h-full flex flex-col justify-center px-stack-lg">
          <h1 className="font-headline-xl text-headline-xl text-on-surface mb-2">
            Welcome back,<br />
            {user?.username}
          </h1>
          <p className="text-on-surface-variant max-w-md font-body-lg text-body-lg">
            Let's jump back into your Minecraft PVP training. Your next session is ready.
          </p>
        </div>
      </section>

      {/* Bento Grid */}
      <div className="grid grid-cols-12 gap-gutter">
        {/* Active Training */}
        <div className="col-span-12 lg:col-span-8">
          <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase mb-4 tracking-widest select-none">
            Active Training
          </h2>
          {activeCourse ? (
            <div className="bg-[#141414] thin-border rounded-lg p-stack-lg relative overflow-hidden">
              <div className="flex flex-col md:flex-row gap-gutter relative z-10">
                <div className="w-full md:w-1/3 aspect-video bg-[#0a0a0a] rounded thin-border overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <span className="text-5xl">{getGamemodeEmoji(activeCourse.gamemode)}</span>
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="px-2 py-0.5 rounded-[4px] bg-[rgba(224,60,60,0.15)] text-[#e03c3c] text-[10px] font-bold uppercase tracking-tighter mb-2 inline-block">
                        {activeCourse.difficulty}
                      </span>
                      <h3 className="font-headline-lg text-headline-lg text-on-surface leading-tight">
                        {activeCourse.title}
                      </h3>
                    </div>
                    <span className="font-headline-sm text-headline-sm text-[#e03c3c]">
                      {activeCourse.percentage ?? 0}%
                    </span>
                  </div>
                  <div className="w-full h-1 bg-surface-container rounded-full mb-6">
                    <div
                      className="h-full bg-error rounded-full transition-all duration-500"
                      style={{ width: `${activeCourse.percentage ?? 0}%` }}
                    ></div>
                  </div>
                  <div className="flex gap-4">
                    <button
                      onClick={handleResumeLesson}
                      className="bg-[#e03c3c] hover:brightness-110 active:scale-95 text-white font-button text-button px-6 py-3 rounded-[4px] flex items-center gap-2 transition-all"
                    >
                      Resume Lesson
                      <span className="material-symbols-outlined text-[18px]">play_arrow</span>
                    </button>
                    <button
                      onClick={() => navigate(`/courses/${activeCourse.id}`)}
                      className="border border-white/10 hover:bg-white/5 text-on-surface font-button text-button px-6 py-3 rounded-[4px] transition-all"
                    >
                      View Syllabus
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[#141414] thin-border rounded-lg p-stack-lg flex flex-col items-center justify-center text-center h-[200px]">
              <span className="material-symbols-outlined text-[48px] text-on-surface-variant mb-2">school</span>
              <p className="text-on-surface-variant mb-4">No active courses. Start learning today!</p>
              <button
                onClick={() => navigate('/courses')}
                className="bg-[#e03c3c] hover:brightness-110 text-white font-button text-button px-6 py-2.5 rounded-[4px] font-semibold transition-all"
              >
                Browse Courses
              </button>
            </div>
          )}
        </div>

        {/* Global Ranking */}
        <div className="col-span-12 lg:col-span-4">
          <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase mb-4 tracking-widest select-none">
            Global Ranking
          </h2>
          <div className="bg-[#141414] thin-border rounded-lg p-gutter h-[calc(100%-40px)] flex flex-col justify-between select-none">
            <div className="space-y-4">
              {topRanks.map((rankRow, index) => (
                <div
                  key={rankRow.userId}
                  className="flex items-center justify-between p-3 bg-surface-container thin-border rounded"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-headline-sm text-headline-sm text-outline">0{index + 1}</span>
                    <span className="font-button text-button">{rankRow.username}</span>
                  </div>
                  <span className="text-[#e03c3c] font-bold">{rankRow.totalXp} XP</span>
                </div>
              ))}
              {myRank && (
                <div className="flex items-center justify-between p-3 border-l-2 border-error bg-surface-variant rounded">
                  <div className="flex items-center gap-3">
                    <span className="font-headline-sm text-headline-sm text-on-surface">{myRank.rank_position}</span>
                    <span className="font-button text-button">{user?.username}</span>
                  </div>
                  <span className="text-[#e03c3c] font-bold">{myRank.total_xp} XP</span>
                </div>
              )}
            </div>
            <Link
              to="/leaderboard"
              className="text-on-surface-variant text-[12px] flex items-center justify-center gap-2 hover:text-on-surface mt-4 transition-colors"
            >
              View Full Leaderboard
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Recommended Courses */}
      <div className="col-span-12 mt-stack-lg select-none">
        <div className="flex justify-between items-end mb-6">
          <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">
            Recommended Courses
          </h2>
          <Link to="/courses" className="text-[#e03c3c] font-button text-button hover:underline">
            Browse All
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
          {recommendedCourses.map((course, idx) => (
            <div
              key={course.id}
              onClick={() => navigate(`/courses/${course.id}`)}
              className="bg-[#141414] thin-border rounded-lg overflow-hidden group hover:border-white/20 transition-all cursor-pointer fade-in-card"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="h-40 relative overflow-hidden bg-[#151515] flex items-center justify-center">
                <span className="text-5xl">{getGamemodeEmoji(course.gamemode)}</span>
                <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${
                  course.difficulty === 'beginner'
                    ? 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]'
                    : 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]'
                }`}>
                  {course.difficulty}
                </div>
              </div>
              <div className="p-stack-md">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${getGamemodeColor(course.gamemode)}`}>
                    {getGamemodeLabel(course.gamemode)}
                  </span>
                </div>
                <h4 className="font-headline-sm text-headline-sm text-on-surface mb-1 truncate">
                  {course.title}
                </h4>
                <p className="text-on-surface-variant text-[12px] line-clamp-2">
                  {course.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Highlights */}
      <div className="col-span-12 mt-stack-lg select-none">
        <div className="flex justify-between items-end mb-6">
          <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">
            Recent Community Highlights
          </h2>
          <Link to="/highlights" className="text-[#e03c3c] font-button text-button hover:underline">
            View All Feed
          </Link>
        </div>
        {highlights.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-stack-md">
            {highlights.map((clip, idx) => (
              <div
                key={clip.id}
                onClick={() => navigate(`/highlights`)}
                className="aspect-video thin-border bg-surface-container-low rounded-lg overflow-hidden relative group cursor-pointer fade-in-card"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <img
                  alt={clip.title}
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                  src={clip.thumbnailUrl}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                  <span className="material-symbols-outlined text-white text-4xl">play_circle</span>
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center bg-black/50 px-1.5 py-0.5 rounded text-[10px]">
                  <span className="font-bold">0:45</span>
                  <span className="text-on-surface-variant truncate max-w-[80px]">
                    by @{clip.user?.username || 'user'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#141414] thin-border rounded-lg p-gutter text-center text-on-surface-variant">
            No clips posted yet. Be the first to share a highlight!
          </div>
        )}
      </div>
    </div>
  );
}